import {
  ButtonDS,
  GlobalIcon,
  RadioButton,
} from "get-life-storybook-ts";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import PhotoIDDS, { PhotoIDDSTranslationsT } from "./PhotoIDDS";
import { I18nContext } from "../../../context/i18n.context";
import { GetlifeContext } from "../../../context/GetLifeContext.context";
import HttpLeadRepository from "../../../api/request/Lead/Lead.service";
import { GetLeadDocumentationI, LeadDocumentationTypeT } from "../../../api/request/Lead/Interfaces/GetLeadDocumentation";
import { formatPhone } from "../../../utils/formatPhone";
import HttpIdCardRepository from "../../../api/request/IdCard/IdCard.service";
import {
  trackQuestionAnswered,
  trackSubmissionError,
  trackValidationError,
} from "../../../helpers/Tracking";

import DNI_FRONT from "../../../assets/img/examples/DNI_FRONT.png";
import DNI_BACK from "../../../assets/img/examples/DNI_BACK.png";
import SELFIE_EXAMPLE from "../../../assets/img/examples/SELFIE.png";

interface IdentificationContentI {
  leadId?: string;
  onComplete?: () => void;
}

/**
 * Componente que contiene el contenido de identificación manual
 * Extraído del modal para poder usarse tanto dentro del modal como a pantalla completa
 */
const IdentificationContent = ({
  leadId: leadIdProp,
  onComplete,
}: IdentificationContentI) => {
  const {
    state: { translate },
  } = useContext(I18nContext);
  const { token, leadId: leadIdFromContext } = useContext(GetlifeContext);
  const navigate = useNavigate();

  // Usar leadId de props o del contexto
  const leadId = leadIdProp || leadIdFromContext;

  const [frontal, setFrontal] = useState("");
  const [back, setBack] = useState("");
  const [selfie, setSelfie] = useState("");
  const [option, setOption] = useState<LeadDocumentationTypeT>("UNKNOWN");
  const [documentation, setDocumentation] = useState<GetLeadDocumentationI>(
    {} as GetLeadDocumentationI
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const leadRepository = new HttpLeadRepository(leadId, token);
  const idCardRepository = new HttpIdCardRepository(leadId, token);

  // Telemetría v3: cualquier fallo al subir, cambiar o enviar la documentación.
  const reportingErrors = async <T,>(call: () => Promise<T>): Promise<T> => {
    try {
      return await call();
    } catch (error) {
      trackSubmissionError(error);
      throw error;
    }
  };

  const uploadImageToServer = async (type: string, base64: string) => {
    setIsLoading(true);
    await reportingErrors(() =>
      leadRepository.postLeadDocumentationUploadImage(leadId, {
        leadId: leadId,
        content: base64,
        type,
      })
    );
    await fetchDocumentation();
    setIsLoading(false);
  };

  const deleteFile = async (type: string) => {
    setIsLoading(true);
    await leadRepository.deleteLeadDocumentationDeleteImage(leadId, type);
    if (type === "FRONT") setFrontal("");
    if (type === "BACK") setBack("");
    if (type === "SELFIE") setSelfie("");
    await fetchDocumentation();
    setIsLoading(false);
  };

  const toggleDocumentType = async (type: LeadDocumentationTypeT) => {
    setIsLoading(true);
    if (option === "UNKNOWN") {
      await reportingErrors(() =>
        leadRepository.postLeadDocumentationDocumentationType(leadId, type)
      );
    } else {
      await reportingErrors(() =>
        leadRepository.putLeadDocumentationDocumentationType(leadId, type)
      );
    }
    setIsLoading(false);
    setOption(type);
  };

  const fetchDocumentation = async () => {
    const response = await leadRepository.getLeadDocumentation(leadId);
    setDocumentation(response as GetLeadDocumentationI);
    setOption(response?.documentationType as LeadDocumentationTypeT);
    response?.documents?.forEach((d) => {
      const { type, remoteUrl } = d;
      if (type === "FRONT") setFrontal(remoteUrl);
      if (type === "BACK") setBack(remoteUrl);
      if (type === "SELFIE") setSelfie(remoteUrl);
    });
  };

  const sendToReview = async () => {
    setIsLoading(true);
    await reportingErrors(() => leadRepository.postLeadDocumentationSave(leadId));
    trackQuestionAnswered("idCard", "postpayment");
    setIsLoading(false);
    navigate("/payment/4");
  };

  useEffect(() => {
    if (leadId) {
      fetchDocumentation();
    }
  }, [leadId]);

  // La revisión de la documentación la marca con error (foto ilegible, no coincide…).
  useEffect(() => {
    if (documentation?.globalStatus === "ERRORED") {
      trackValidationError("documentation", "api");
    }
  }, [documentation?.globalStatus]);

  const fileTranslations = {
    validations: translate("identification.modal.validations"),
    uploading: translate("identification.modal.uploading"),
    loading: translate("identification.modal.loading"),
    completed: translate("identification.modal.completed"),
    remove: translate("identification.modal.remove"),
    see: translate("identification.modal.see"),
    cancel: translate("identification.modal.cancel"),
    delete: translate("identification.modal.delete"),
    deleteConfirmation: translate("identification.modal.deleteConfirmation"),
    errorFormat: translate("identification.modal.error.format"),
    errorSize: translate("identification.modal.error.size"),
  };

  const isRadioDisabled = !!(frontal || back || selfie) || isLoading;
  const globalStatus = documentation.globalStatus;

  const isSaveDisabled = ((): boolean => {
    if (option === "PA" && frontal && selfie) {
      return false;
    } else if (frontal && back && selfie) {
      return false;
    }
    return true;
  })();

  if (isSaved) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className="p-[8px] bg-[#ECECFE] rounded-full w-fit mb-[16px]">
          <GlobalIcon
            iconName="CheckedIcon"
            size="XS"
            color="var(--primary-color)"
          />
        </div>
        <p className="BodyM text-[#424242]">
          {translate("identification.modal.saved")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Información del asegurado */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-[16px] border-b border-b-[#E2E2E2] pb-[16px]">
        <p className="flex flex-col gap-[4px] BodyM font-medium text-[#7B7B7B]">
          <span className="whitespace-nowrap">
            {translate("identification.modal.insuredInformation")}
          </span>
          <span className="text-[#424242]">{documentation?.fullName}</span>
        </p>
        <p className="flex flex-col gap-[4px] BodyM font-medium text-[#7B7B7B]">
          <span className="whitespace-nowrap">
            {translate("identification.modal.email")}
          </span>
          <span className="text-[#424242] break-all">{documentation?.email}</span>
        </p>
        <p className="flex flex-col gap-[4px] BodyM font-medium text-[#7B7B7B]">
          <span className="whitespace-nowrap">
            {translate("identification.modal.phone")}
          </span>
          <span className="text-[#424242]">
            {documentation?.phone ? formatPhone(documentation.phone) : ""}
          </span>
        </p>
      </div>

      {/* Selección del tipo de documento */}
      <div className="pb-[8px] border-b border-b-[#E2E2E2]">
        <p className="text-[var(--dark-primary-color)] BodyM font-medium">
          {translate("identification.modal.selectDocumentType")}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-[16px] md:gap-[40px] px-[16px] mt-[16px]">
          <RadioButton
            checked={option === "ID"}
            value={"ID"}
            onChange={() => toggleDocumentType("ID")}
            label={translate("identification.modal.dni")}
            variant="SimpleRadio"
            disabled={isRadioDisabled}
          />
          <RadioButton
            checked={option === "RP"}
            value={"RP"}
            onChange={() => toggleDocumentType("RP")}
            label={translate("identification.modal.nifnie")}
            variant="SimpleRadio"
            disabled={isRadioDisabled}
          />
          <RadioButton
            checked={option === "PA"}
            value={"PA"}
            onChange={() => toggleDocumentType("PA")}
            label={translate("identification.modal.passport")}
            variant="SimpleRadio"
            disabled={isRadioDisabled}
          />
        </div>
      </div>

      {/* Upload de imágenes */}
      {option !== "UNKNOWN" && (
        <div>
          <p className="BodyM font-medium text-[var(--dark-primary-color)] mb-[16px]">
            {translate("identification.modal.clientIdentification")}
          </p>
          <div className={`grid gap-[16px] md:gap-[32px] ${option === "PA" ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-3"}`}>
            <PhotoIDDS
              image={frontal}
              setImage={setFrontal}
              onLoadCallback={async (base64: string) => {
                await uploadImageToServer("FRONT", base64);
              }}
              translations={(() => {
                const translations = {
                  ...fileTranslations,
                } as PhotoIDDSTranslationsT;
                if (option === "ID") {
                  translations.steps = translate(
                    "identification.modal.steps.dni.front"
                  );
                  translations.modal = translate("identification.modal.dni.front");
                }
                if (option === "RP") {
                  translations.steps = translate(
                    "identification.modal.steps.nifnie.front"
                  );
                  translations.modal = translate(
                    "identification.modal.nifnie.front"
                  );
                }
                if (option === "PA") {
                  translations.steps = translate(
                    "identification.modal.steps.passport.front"
                  );
                  translations.modal = translate("identification.modal.passport");
                }
                return translations;
              })()}
              deleteFile={async () => {
                deleteFile("FRONT");
              }}
              error={globalStatus === "ERRORED"}
              isRemovable={
                globalStatus === "PENDING" || globalStatus === "ERRORED"
              }
              exampleImage={DNI_FRONT}
            />
            {option !== "PA" && (
              <PhotoIDDS
                image={back}
                setImage={setBack}
                onLoadCallback={async (base64: string) => {
                  await uploadImageToServer("BACK", base64);
                }}
                translations={(() => {
                  const translations = {
                    ...fileTranslations,
                  } as PhotoIDDSTranslationsT;
                  if (option === "ID") {
                    translations.steps = translate(
                      "identification.modal.steps.dni.back"
                    );
                    translations.modal = translate("identification.modal.dni.back");
                  }
                  if (option === "RP") {
                    translations.steps = translate(
                      "identification.modal.steps.nifnie.back"
                    );
                    translations.modal = translate(
                      "identification.modal.nifnie.back"
                    );
                  }
                  return translations;
                })()}
                deleteFile={async () => {
                  deleteFile("BACK");
                }}
                error={globalStatus === "ERRORED"}
                isRemovable={
                  globalStatus === "PENDING" || globalStatus === "ERRORED"
                }
                exampleImage={DNI_BACK}
              />
            )}
            <PhotoIDDS
              image={selfie}
              setImage={setSelfie}
              onLoadCallback={async (base64: string) => {
                await uploadImageToServer("SELFIE", base64);
              }}
              translations={(() => {
                const translations = {
                  ...fileTranslations,
                } as PhotoIDDSTranslationsT;
                translations.steps = translate("identification.modal.steps.selfie");
                translations.modal = translate("identification.modal.selfie");
                return translations;
              })()}
              deleteFile={async () => {
                deleteFile("SELFIE");
              }}
              error={globalStatus === "ERRORED"}
              isRemovable={
                globalStatus === "PENDING" || globalStatus === "ERRORED"
              }
              exampleImage={SELFIE_EXAMPLE}
            />
          </div>
        </div>
      )}

      {/* Botón de guardar */}
      {(globalStatus === "PENDING" || globalStatus === "IN_REVIEW") && (
        <div className="flex justify-center md:justify-end mt-[16px]">
          <ButtonDS
            label={translate("identification.modal.save.cta")}
            className="w-full md:w-fit md:min-w-[259px]"
            onClick={sendToReview}
            disabled={isSaveDisabled || isLoading}
          />
        </div>
      )}
    </div>
  );
};

export default IdentificationContent;
